import React, { useState, useMemo } from 'react';
import { 
  MapPin, 
  Filter,
  CheckCircle,
  Search,
  Target,
  Star,
  Award,
  Users,
  DollarSign,
  TrendingUp,
  Eye,
  Edit3,
  Trash2,
  ExternalLink,
  X,
  Trophy,
  Zap,
  Undo,
  Lock,
  Unlock,
  Clock,
  Shield,
  FileText
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { getUpdateHistory } from '../../utils/collegeAIUpdate';
import '../../styles/neo-brutalist-colleges.css';

const BrutalistCollegeGrid = ({
  colleges,
  selectedColleges,
  onSelectionChange,
  onCollegeRemove,
  onEditCollege, // Add edit handler
  onAIUpdate, // Add AI update handler
  onUndo = () => {}, // Add undo handler with default
  onManageLocks = () => {}, // Add lock manager with default
  onEssayRequirements = () => {}, // Add essay requirements handler with default

  updatingCollegeId, // ID of college being updated
  showSearch = true,
  showFilters = true,
  viewMode = 'student', // 'global' or 'student'
  isParent = false,
  currentUser = null,
  familyUsers = [],
  sortBy = 'name',
  sortDirection = 'asc',

  // Category lock props
  categoryInfo = null,
  lockedCategories = {},
  onToggleLock = () => {}
}) => {
  const navigate = useNavigate();
  const { addCollege } = useCollegeContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    type: 'all',
    hasRanking: 'all'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Add to List Modal State
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [selectedListCategory, setSelectedListCategory] = useState('target');
  const [selectedListStudent, setSelectedListStudent] = useState(null);
  const [loading, setLoading] = useState(false);

  // Get children for student selection
  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  // Filter and sort colleges based on search, filters, and sort options
  const filteredColleges = useMemo(() => {
    // First filter
    const filtered = colleges.filter(college => {
      // Search filter
      if (searchTerm && !college.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Category filter
      if (filters.category !== 'all' && college.category !== filters.category) {
        return false;
      }
      
      // Type filter
      if (filters.type !== 'all' && college.type !== filters.type) {
        return false;
      }
      
      // Ranking filter
      if (filters.hasRanking === 'ranked' && !hasAnyRanking(college)) {
        return false;
      }
      if (filters.hasRanking === 'unranked' && hasAnyRanking(college)) {
        return false;
      }
      
      return true;
    });

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'ranking':
          const aRank = a.usnwr_ranking || a.liberal_arts_ranking || 999;
          const bRank = b.usnwr_ranking || b.liberal_arts_ranking || 999;
          comparison = aRank - bRank;
          break;
        case 'engineering_ranking':
          const aEng = a.engineering_ranking || 999;
          const bEng = b.engineering_ranking || 999;
          comparison = aEng - bEng;
          break;
        case 'nursing_ranking':
          const aNurs = a.nursing_ranking || 999;
          const bNurs = b.nursing_ranking || 999;
          comparison = aNurs - bNurs;
          break;
        case 'business_ranking':
          const aBus = a.business_ranking || 999;
          const bBus = b.business_ranking || 999;
          comparison = aBus - bBus;
          break;
        case 'cs_ranking':
          const aCS = a.cs_ranking || 999;
          const bCS = b.cs_ranking || 999;
          comparison = aCS - bCS;
          break;
        case 'liberal_arts_ranking':
          const aLA = a.liberal_arts_ranking || 999;
          const bLA = b.liberal_arts_ranking || 999;
          comparison = aLA - bLA;
          break;
        case 'acceptance_rate':
          const aRate = a.acceptance_rate || 100;
          const bRate = b.acceptance_rate || 100;
          comparison = aRate - bRate;
          break;
        case 'tuition':
          const aTuition = a.tuition_out_of_state || a.tuition_in_state || a.tuition || 0;
          const bTuition = b.tuition_out_of_state || b.tuition_in_state || b.tuition || 0;
          comparison = aTuition - bTuition;
          break;
        case 'enrollment':
          const aEnroll = a.student_enrollment || 0;
          const bEnroll = b.student_enrollment || 0;
          comparison = bEnroll - aEnroll; // Largest first by default
          break;
        default:
          return 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [colleges, searchTerm, filters, sortBy, sortDirection]);

  // Check if college has any ranking
  const hasAnyRanking = (college) => {
    return !!(college.usnwr_ranking || 
             college.engineering_ranking || 
             college.nursing_ranking ||
             college.business_ranking ||
             college.cs_ranking ||
             college.liberal_arts_ranking);
  };

  // Toggle college selection
  const toggleSelection = (collegeId) => {
    const newSelection = selectedColleges.includes(collegeId)
      ? selectedColleges.filter(id => id !== collegeId)
      : [...selectedColleges, collegeId];
    onSelectionChange(newSelection);
  };

  // Select all filtered colleges
  const selectAll = () => {
    const filteredIds = filteredColleges.map(c => c.id);
    const newSelection = [...new Set([...selectedColleges, ...filteredIds])];
    onSelectionChange(newSelection);
  };

  // Clear selection
  const clearSelection = () => {
    onSelectionChange([]);
  };

  // Handle delete
  const handleDelete = async (college) => {
    try {
      await onCollegeRemove(college.id, college.user_id);
      toast.success(`Removed ${college.name}`);
      setShowDeleteConfirm(null);
    } catch {
      toast.error('Failed to remove college');
    }
  };

  // Add to student list functionality
  const addToStudentList = async (globalCollege, student, category = 'other') => {
    try {
      const collegeData = {
        name: globalCollege.name,
        official_name: globalCollege.official_name || globalCollege.name,
        location: globalCollege.location,
        website: globalCollege.website,
        global_college_id: globalCollege.id,
        category: category,
        status: 'researching',
        type: globalCollege.type,
        user_id: student.id,
        family_id: student.family_id,
        // Copy additional fields
        usnwr_ranking: globalCollege.usnwr_ranking,
        engineering_ranking: globalCollege.engineering_ranking,
        nursing_ranking: globalCollege.nursing_ranking,
        business_ranking: globalCollege.business_ranking,
        cs_ranking: globalCollege.cs_ranking,
        liberal_arts_ranking: globalCollege.liberal_arts_ranking,
        acceptance_rate: globalCollege.acceptance_rate,
        tuition_in_state: globalCollege.tuition_in_state,
        tuition_out_of_state: globalCollege.tuition_out_of_state,
        student_population: globalCollege.student_population,
        academic_focus: globalCollege.academic_focus
      };

      await addCollege(collegeData, student.id);
    } catch (error) {
      console.error('Error adding college to student list:', error);
      throw error;
    }
  };

  // Handle Add to List
  const handleAddToList = async () => {
    if (selectedColleges.length === 0) {
      toast.error('Please select at least one college');
      return;
    }
    
    // For students, use current user; for parents, use selected student
    const targetStudent = isParent ? selectedListStudent : currentUser;
    if (!targetStudent) {
      toast.error('Unable to determine target student');
      return;
    }
    
    try {
      setLoading(true);
      let successCount = 0;
      
      for (const collegeId of selectedColleges) {
        const college = colleges.find(c => c.id === collegeId);
        if (college) {
          await addToStudentList(college, targetStudent, selectedListCategory);
          successCount++;
        }
      }
      
      const listOwner = isParent ? `${targetStudent.displayName || targetStudent.name}'s` : 'your';
      toast.success(`Added ${successCount} colleges to ${listOwner} ${selectedListCategory} list`);
      onSelectionChange([]); // Clear selection
      setShowAddToListModal(false);
    } catch (error) {
      console.error('Error adding colleges to list:', error);
      toast.error('Failed to add colleges to list');
    } finally {
      setLoading(false);
    }
  };

  // Navigation to college strategy page
  const handleCollegeClick = (college) => {
    if (viewMode === 'global') {
      // For global colleges, navigate to the global college view using the college's ID
      navigate(`/global-college/${college.id}`);
    } else {
      // For student colleges, navigate to college strategy page using the college's ID
      navigate(`/college/${college.id}`);
    }
  };

  return (
    <div className="brutalist-colleges-container">
      {/* Search and Filters */}
      {(showSearch || showFilters) && (
        <section className="brutalist-search-section">
          {showSearch && (
            <input
              type="text"
              className="brutalist-search-input"
              placeholder="Search colleges..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          )}
          
          {showFilters && (
            <div className="flex gap-3">
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="brutalist-filter-btn"
                style={{ textTransform: 'none' }}
              >
                <option value="all">All Categories</option>
                <option value="reach">reach</option>
                <option value="target">Target</option>
                <option value="safety">Safety</option>
                <option value="other">Other</option>
              </select>
              
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="brutalist-filter-btn"
                style={{ textTransform: 'none' }}
              >
                <option value="all">All Types</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
              
              <select
                value={filters.hasRanking}
                onChange={(e) => setFilters(prev => ({ ...prev, hasRanking: e.target.value }))}
                className="brutalist-filter-btn"
                style={{ textTransform: 'none' }}
              >
                <option value="all">All Rankings</option>
                <option value="ranked">Ranked</option>
                <option value="unranked">Unranked</option>
              </select>
            </div>
          )}
        </section>
      )}

      {/* Selection Summary and Add to List Button */}
      {selectedColleges.length > 0 && (
        <div className="mb-6 p-4 bg-azure-50 border-2 border-azure-500 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-azure-600" />
              <span className="font-bold text-azure-900">
                {selectedColleges.length} college{selectedColleges.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center gap-3">
              {viewMode === 'global' && (
                <button
                  onClick={() => setShowAddToListModal(true)}
                  className="px-3 sm:px-4 py-2 text-sm font-bold text-white bg-emerald-500 border-2 border-slate-900 rounded-md hover:bg-emerald-600 flex items-center gap-2"
                >
                  <Trophy className="w-4 h-4" />
                  Add to {isParent ? 'List' : 'My Schools'} ({selectedColleges.length})
                </button>
              )}
              <button
                onClick={clearSelection}
                className="text-sm text-azure-600 hover:text-azure-700 font-bold uppercase"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results count and select all */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <span className="text-lg font-bold">
            {categoryInfo ? categoryInfo.label : `${filteredColleges.length} college${filteredColleges.length !== 1 ? 's' : ''}`}
          </span>
          {searchTerm && (
            <span className="text-stone-600">
              matching "{searchTerm}"
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Lock button for category views */}
          {categoryInfo && categoryInfo.category && filteredColleges.length > 0 && (
            <button
              onClick={() => {
                onToggleLock(categoryInfo.category, !lockedCategories?.[categoryInfo.category]);
              }}
              className="px-3 sm:px-4 py-2 text-sm font-bold border-2 rounded-md flex items-center gap-2"
              style={{
                background: lockedCategories?.[categoryInfo.category] ? '#3b82f6' : 'white',
                borderColor: lockedCategories?.[categoryInfo.category] ? '#3b82f6' : '#1e293b',
                color: lockedCategories?.[categoryInfo.category] ? 'white' : '#1e293b'
              }}
            >
              {lockedCategories?.[categoryInfo.category] ? (
                <>
                  <Lock className="w-4 h-4" />
                  <span>LOCKED</span>
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4" />
                  <span>LOCK SELECTIONS</span>
                </>
              )}
            </button>
          )}

          {filteredColleges.length > 0 && (
            <button
              onClick={selectAll}
              className="brutalist-filter-btn"
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Select All Visible
            </button>
          )}
        </div>
      </div>

      {/* Colleges Grid */}
      <div className="brutalist-colleges-grid">
        {filteredColleges.map((college) => (
          <BrutalistCollegeCard
            key={college.id}
            college={college}
            isSelected={selectedColleges.includes(college.id)}
            onToggleSelection={toggleSelection}
            onDelete={() => setShowDeleteConfirm(college.id)}
            {...(viewMode === 'global' && {
              onEditCollege: onEditCollege, // Pass edit handler
              onAIUpdate: onAIUpdate, // Pass AI update handler
              isUpdating: updatingCollegeId === college.id || updatingCollegeId === college.global_college_id, // Check if this college is updating
              hasUpdateHistory: getUpdateHistory ? getUpdateHistory(college.global_college_id || college.id) !== null : false, // Check history
              onUndo: onUndo, // Pass undo handler
              onManageLocks: onManageLocks, // Pass lock manager
              onEssayRequirements: onEssayRequirements // Pass essay requirements handler
            })}
            onCollegeClick={handleCollegeClick}
            viewMode={viewMode}
          />
        ))}
      </div>

      {/* Empty state */}
      {filteredColleges.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-stone-200 border-3 border-slate-900 rounded-lg flex items-center justify-center">
            <Search className="w-8 h-8 text-stone-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">No colleges found</h3>
          <p className="text-stone-600">
            {searchTerm ? 'Try adjusting your search terms' : 'No colleges match your current filters'}
          </p>
        </div>
      )}

      {/* Add to List Modal */}
      {showAddToListModal && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border-3 border-slate-900 rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                Add to College List
              </h2>
              <button
                onClick={() => setShowAddToListModal(false)}
                className="text-stone-600 hover:text-slate-900"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-stone-600 mb-4">
                Adding {selectedColleges.length} college{selectedColleges.length !== 1 ? 's' : ''} to list
              </p>
            </div>

            <div className="space-y-4">
              {isParent && (
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Select Student
                  </label>
                  <select
                    value={selectedListStudent?.id || ''}
                    onChange={(e) => {
                      const student = children.find(c => c.id === e.target.value);
                      setSelectedListStudent(student || null);
                    }}
                    className="w-full p-3 border-2 border-stone-300 rounded-md font-bold"
                  >
                    <option value="">Choose a student...</option>
                    {children.map(child => (
                      <option key={child.id} value={child.id}>
                        {child.displayName || child.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {!isParent && (
                <div className="p-4 bg-azure-50 border border-azure-200 rounded-md">
                  <p className="text-azure-800">
                    Adding to your college list: <strong>{currentUser?.displayName || currentUser?.name || 'My Schools'}</strong>
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Select List Category
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'reach', label: 'reach', icon: Trophy, color: '#fbbf24' },
                    { value: 'target', label: 'Target', icon: Target, color: '#0d9488' },
                    { value: 'safety', label: 'Safety', icon: CheckCircle, color: '#3b82f6' }
                  ].map(category => (
                    <button
                      key={category.value}
                      onClick={() => setSelectedListCategory(category.value)}
                      className={`p-4 border-2 rounded-md transition-all flex flex-col items-center gap-2 ${
                        selectedListCategory === category.value 
                          ? 'border-slate-900 bg-stone-100' 
                          : 'border-stone-300 hover:border-stone-400'
                      }`}
                    >
                      <category.icon className="w-5 sm:w-6 h-5 sm:h-6" style={{ color: category.color }} />
                      <span className="text-sm font-bold text-slate-900">
                        {category.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddToListModal(false)}
                className="px-3 sm:px-4 py-2 text-sm font-bold text-slate-900 bg-stone-200 border-2 border-stone-500 rounded-md hover:bg-stone-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddToList}
                disabled={loading || (isParent && !selectedListStudent)}
                className="px-3 sm:px-4 py-2 text-sm font-bold text-white bg-emerald-500 border-2 border-slate-900 rounded-md hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Adding...' : 'Add to List'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border-3 border-slate-900 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-slate-900 mb-4">
              Confirm Deletion
            </h3>
            <p className="text-stone-600 mb-6">
              Are you sure you want to remove this college from your list? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-3 sm:px-4 py-2 text-sm font-bold text-slate-900 bg-stone-200 border-2 border-stone-500 rounded-md hover:bg-stone-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const college = colleges.find(c => c.id === showDeleteConfirm);
                  if (college) handleDelete(college);
                }}
                className="px-3 sm:px-4 py-2 text-sm font-bold text-white bg-red-500 border-2 border-slate-900 rounded-md hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Brutalist College Card Component
const BrutalistCollegeCard = ({ 
  college, 
  isSelected, 
  onToggleSelection, 
  onDelete, 
  onEditCollege, // Add edit handler prop
  onAIUpdate, // Add AI update handler
  isUpdating, // Is this college being updated
  hasUpdateHistory, // Does this college have undo history
  onUndo, // Undo handler
  onManageLocks, // Manage field locks handler
  onEssayRequirements, // Essay requirements handler
  onCollegeClick,
  viewMode = 'student'
}) => {

  // Helper functions 
  const generateRankingTags = (college) => {
    const tags = [];
    
    // Overall ranking
    if (college.usnwr_ranking) {
      const rankingValue = college.usnwr_ranking.toString().toUpperCase();
      if (rankingValue === 'UNWR' || rankingValue.includes('UNWR')) {
        tags.push({
          type: 'unranked',
          label: 'US News #UNWR',
          className: 'brutalist-tag unranked'
        });
      } else {
        tags.push({
          type: 'overall-ranking',
          label: `US News #${college.usnwr_ranking}`,
          className: 'brutalist-tag overall-ranking'
        });
      }
    }
    
    // Engineering ranking
    if (college.engineering_ranking) {
      const engRanking = college.engineering_ranking.toString().toUpperCase();
      if (engRanking === 'UNWR' || engRanking.includes('UNWR')) {
        tags.push({
          type: 'unranked',
          label: 'Engineering US News',
          className: 'brutalist-tag unranked'
        });
      } else {
        tags.push({
          type: 'engineering-ranking',
          label: `Engineering #${college.engineering_ranking}`,
          className: 'brutalist-tag engineering-ranking'
        });
      }
    }
    
    // Nursing ranking
    if (college.nursing_ranking) {
      const nursingRanking = college.nursing_ranking.toString().toUpperCase();
      if (nursingRanking === 'UNWR' || nursingRanking.includes('UNWR')) {
        tags.push({
          type: 'unranked',
          label: 'Nursing US News',
          className: 'brutalist-tag unranked'
        });
      } else {
        tags.push({
          type: 'nursing-ranking',
          label: `Nursing #${college.nursing_ranking}`,
          className: 'brutalist-tag nursing-ranking'
        });
      }
    }
    
    // Business ranking
    if (college.business_ranking) {
      const businessRanking = college.business_ranking.toString().toUpperCase();
      if (businessRanking === 'UNWR' || businessRanking.includes('UNWR')) {
        tags.push({
          type: 'unranked',
          label: 'Business US News',
          className: 'brutalist-tag unranked'
        });
      } else {
        tags.push({
          type: 'business-ranking',
          label: `Business #${college.business_ranking}`,
          className: 'brutalist-tag business-ranking'
        });
      }
    }
    
    // Computer Science ranking
    if (college.cs_ranking) {
      const csRanking = college.cs_ranking.toString().toUpperCase();
      if (csRanking === 'UNWR' || csRanking.includes('UNWR')) {
        tags.push({
          type: 'unranked',
          label: 'CS US News',
          className: 'brutalist-tag unranked'
        });
      } else {
        tags.push({
          type: 'cs-ranking',
          label: `CS #${college.cs_ranking}`,
          className: 'brutalist-tag cs-ranking'
        });
      }
    }
    
    // Liberal Arts ranking
    if (college.liberal_arts_ranking) {
      const liberalArtsRanking = college.liberal_arts_ranking.toString().toUpperCase();
      if (liberalArtsRanking === 'UNWR' || liberalArtsRanking.includes('UNWR')) {
        tags.push({
          type: 'unranked',
          label: 'Liberal Arts US News',
          className: 'brutalist-tag unranked'
        });
      } else {
        tags.push({
          type: 'liberal-arts-ranking',
          label: `Liberal Arts #${college.liberal_arts_ranking}`,
          className: 'brutalist-tag liberal-arts-ranking'
        });
      }
    }
    
    // If no rankings at all, show unranked
    if (tags.length === 0) {
      tags.push({
        type: 'unranked',
        label: 'Unranked',
        className: 'brutalist-tag unranked'
      });
    }
    
    return tags;
  };

  const generateAdditionalTags = (college) => {
    const tags = [];
    
    // Type tag
    if (college.type) {
      tags.push({
        type: `type-${college.type}`,
        label: college.type === 'public' ? 'Public' : 'Private',
        className: `brutalist-tag type-${college.type}`
      });
    }

    // Academic focus tag
    if (college.academic_focus) {
      tags.push({
        type: 'academic-focus',
        label: college.academic_focus,
        className: 'brutalist-tag academic-focus'
      });
    }
    
    return tags;
  };

  const rankingTags = generateRankingTags(college);
  const additionalTags = generateAdditionalTags(college);

  return (
    <article 
      className={`brutalist-college-card ${isSelected ? 'selected' : ''}`}
    >
      {/* Header - Clickable for navigation */}
      <header 
        className="brutalist-college-header cursor-pointer"
        onClick={() => onCollegeClick(college)}
      >
        <div className="flex-1">
          <h2 className="brutalist-college-name">{college.name}</h2>
          {college.location && (
            <div className="brutalist-college-location">
              <span className="brutalist-location-icon"></span>
              {college.location}
            </div>
          )}
        </div>
        {viewMode === 'global' && (
          <input 
            type="checkbox" 
            className="brutalist-college-checkbox"
            checked={isSelected}
            onChange={() => onToggleSelection(college.id)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </header>

      {/* Body - Also clickable for navigation */}
      <div 
        className="brutalist-college-body cursor-pointer"
        onClick={() => onCollegeClick(college)}
      >
        {/* Tags */}
        <div className="brutalist-college-tags">
          {/* Ranking tags first (most important) */}
          {rankingTags.map((tag, index) => (
            <span key={`ranking-${index}`} className={tag.className}>
              {tag.label}
            </span>
          ))}
          
          {/* Additional tags */}
          {additionalTags.map((tag, index) => (
            <span key={`additional-${index}`} className={tag.className}>
              {tag.label}
            </span>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="brutalist-college-stats">
          {college.acceptance_rate && (
            <div className="brutalist-stat-item">
              <span className="brutalist-stat-label">Accept Rate</span>
              <span className="brutalist-stat-value">{(college.acceptance_rate * 100).toFixed(1)}%</span>
            </div>
          )}
          
          {(college.tuition_out_of_state || college.tuition_in_state || college.tuition) && (
            <div className="brutalist-stat-item">
              <span className="brutalist-stat-label">Tuition</span>
              <span className="brutalist-stat-value">
                ${(college.tuition_out_of_state || college.tuition_in_state || college.tuition).toLocaleString()}
              </span>
            </div>
          )}
          
          {college.student_population && (
            <div className="brutalist-stat-item">
              <span className="brutalist-stat-label">Students</span>
              <span className="brutalist-stat-value">
                {college.student_population.toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-stone-200">
          <div className="flex items-center gap-3">
            {college.website && (
              <a
                href={college.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-sm font-bold text-indigo-500 hover:text-indigo-700 flex items-center gap-1 uppercase"
              >
                <ExternalLink className="w-4 h-4" />
                Website
              </a>
            )}
            
            {viewMode === 'global' && onEditCollege && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCollege(college);
                }}
                className="text-sm font-bold text-azure-500 hover:text-azure-700 flex items-center gap-1 uppercase"
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </button>
            )}
            
            {viewMode === 'global' && onEssayRequirements && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEssayRequirements(college);
                }}
                className="text-sm font-bold text-indigo-500 hover:text-indigo-700 flex items-center gap-1 uppercase"
                title="Manage essay requirements"
              >
                <FileText className="w-4 h-4" />
                Essays
              </button>
            )}
            
            {viewMode === 'global' && onAIUpdate && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAIUpdate(college);
                  }}
                  disabled={isUpdating}
                  className={`text-sm font-bold flex items-center gap-1 uppercase ${
                    isUpdating 
                      ? 'text-stone-400 cursor-not-allowed' 
                      : 'text-violet-500 hover:text-violet-700'
                  }`}
                  title={isUpdating ? 'Update in progress...' : 'Update college data with AI'}
                >
                  {isUpdating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-stone-400 border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      AI Update
                    </>
                  )}
                </button>
                
                {hasUpdateHistory && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Use global_college_id if available (for student colleges), otherwise use id
                      onUndo(college.global_college_id || college.id);
                    }}
                    className="text-sm font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1 uppercase"
                    title="Undo last AI update"
                  >
                    <Undo className="w-4 h-4" />
                    Undo
                  </button>
                )}
                
                {onManageLocks && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageLocks(college);
                    }}
                    className="text-sm font-bold text-stone-600 hover:text-stone-700 flex items-center gap-1"
                    title="Manage protected fields"
                  >
                    <Shield className="w-3 h-3" />
                  </button>
                )}
              </>
            )}
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-sm font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase"
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
        </div>
      </div>
    </article>
  );
};

export default BrutalistCollegeGrid;
